import React from 'react';

import {AnalysisGraphQL, AnalysisViewGraphQL, isAnalysisInputStepType} from 'analysis-tool/AnalysisTypes';
import {isViewTerminalStepType} from 'analysis-tool/data/LineageStepTypes';
import {getOutputDataForView} from 'analysis-tool/data-view/AnalysisViewUtils';
import isAnalysisReadOnly from 'analysis-tool/isAnalysisReadOnly';
import {
  AnalysisRightSidebarItem,
  AnalysisRightSidebarItemType,
} from 'analysis-tool/right-sidebar/RightSidebarTypes';
import AddTransformStepDropdown from 'analysis-tool/ui/charting/AddTransformStepDropdown';
import {BLOCKED_BY_DOWNSTREAM_FLAG_ROWS_MESSAGE} from 'analysis-tool/ui/charting/AddTransformStepDropdown';
import {FIX_FAILED_TRANSFORMS_MESSAGE} from 'analysis-tool/ui/charting/AddTransformStepDropdown';
import {getTransformPills} from 'analysis-tool/ui/charting/TransformPillUtils';
import AdditionalEditorSection from 'analysis-tool/ui/components/AdditionalEditorSection';
import {AnalysisMainViewItem, AnalysisMainViewItemType} from 'analysis-tool/ui/main-view/MainViewTypes';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import {PipelineStepDataKind, PipelineStepStatusCode} from 'graphql-client/__generated/graphqlGlobalTypes';
import hasDownstreamFlagRowsStep from 'pipelines/hasDownstreamFlagRowsStep';
import {PipelineStepRawData} from 'pipelines/PipelineTypes';
import {getLegacyStepFromAnalysis} from 'pipelines/StepUtil';

interface EditViewTransformsProps {
  analysis: AnalysisGraphQL;
  view: AnalysisViewGraphQL;
  dataFrameSpec: PipelineStepRawData<PipelineStepDataKind.DATASET>;
  onChangeSelectedRightSidebarItem: (newSelectedRightSidebarItem: AnalysisRightSidebarItem | null) => void;
  selectedRightSidebarItem: AnalysisRightSidebarItem<AnalysisRightSidebarItemType.EDIT_VIEW>;
  onChangeSelectedItem: (newSelectedItem: AnalysisMainViewItem | null) => void;
}

export default function EditViewTransforms({
  analysis,
  view,
  dataFrameSpec,
  onChangeSelectedRightSidebarItem,
  selectedRightSidebarItem,
  onChangeSelectedItem,
}: EditViewTransformsProps): JSX.Element {
  const viewSteps = view.lineage.map(({step}) => getLegacyStepFromAnalysis(analysis, step));
  const allStepsSucceeded = viewSteps.every(
    (step, index) => step.statusCode === PipelineStepStatusCode.SUCCEEDED || index === viewSteps.length - 1
  );
  const blockedByDownstream = hasDownstreamFlagRowsStep(analysis, viewSteps[viewSteps.length - 1]);
  const isTopLevelView = isAnalysisInputStepType(viewSteps[0].definition.stepType);
  const canAddNewSteps = allStepsSucceeded && !blockedByDownstream;
  const addNewStepsDisabledMessage =
    (!allStepsSucceeded ? FIX_FAILED_TRANSFORMS_MESSAGE : undefined) ??
    (blockedByDownstream ? BLOCKED_BY_DOWNSTREAM_FLAG_ROWS_MESSAGE : undefined);

  function beforeDelete(): void {
    onChangeSelectedRightSidebarItem({...selectedRightSidebarItem, isUpdating: true});
    // TODO: We will need to update this to handle allowing this sidebar to be used with other views.
    onChangeSelectedItem({
      type: AnalysisMainViewItemType.DATA_VIEW,
      viewReference: selectedRightSidebarItem.viewReference,
      isUpdating: true,
    });
  }

  function afterDelete(stepId: string): void {
    const deletedStepIndex = view.lineage.findIndex(({step}) => step.id === stepId);
    assert(deletedStepIndex > 0, 'Cannot select view if the first step was deleted');
    const newViewReference = {
      stepId: view.lineage[deletedStepIndex - 1].step.id,
      outputReference: {
        stepDataName: view.lineage[deletedStepIndex - 1].stepOutputReference?.outputStepData?.name!,
        rawDataIndex: 0,
      },
    };
    // TODO: We will need to update this to handle allowing this sidebar to be used with other views.
    // Change the main view first so we don't unmount the right sidebar before updating its viewReference!
    onChangeSelectedItem({type: AnalysisMainViewItemType.DATA_VIEW, viewReference: newViewReference});
    onChangeSelectedRightSidebarItem({
      type: AnalysisRightSidebarItemType.EDIT_VIEW,
      viewReference: newViewReference,
    });
  }

  const deleteProps = isTopLevelView ? {afterDelete, beforeDelete} : undefined;

  // getOutputDataForView works for top level, but for view level the last step in the view lineage is a terminal step,
  // so we need the input data frame instead.
  const endsInTerminalViewStep = isViewTerminalStepType(viewSteps[viewSteps.length - 1].definition.stepType);
  // might be undefined if step is running/blocked, failed, etc.
  const maybeInputDataFrameId = endsInTerminalViewStep ? dataFrameSpec.id : getOutputDataForView(view)?.id;

  return (
    <AdditionalEditorSection
      title="Filters & Transforms"
      addItemElement={
        maybeInputDataFrameId && !isAnalysisReadOnly(analysis) && !isArchived(analysis) ? (
          <AddTransformStepDropdown
            viewSteps={viewSteps}
            dataFrameSpec={dataFrameSpec}
            inputDataFrameId={maybeInputDataFrameId}
            analysis={analysis}
            canAddNewSteps={canAddNewSteps}
            addNewStepsDisabledMessage={addNewStepsDisabledMessage}
            isTopLevelView={isTopLevelView}
          />
        ) : (
          <></>
        )
      }
      pills={getTransformPills(analysis, viewSteps, deleteProps)}
      showTopBorder={false}
    />
  );
}
